/**
 * SSOT למחיר כפול בכרטיסי מאורגנים — ללא שערי חליפין קשיחים ב-FE.
 * מטבע משני = מטבע מקור LDS (USD / EUR / …), לא תמיד דולר.
 * מקורות עיקריים: ourPrice.pricingSnapshot, ourPrice / ourTotalFare, fareDetails.currency.
 */

function pickOurPrice(deal) {
  const root = deal && typeof deal === "object" ? deal : {};
  return (
    root.ourPrice ||
    root.fullDeal?.ourPrice ||
    (root.price && typeof root.price === "object" ? root.price.ourPrice : null) ||
    null
  );
}

function toPositiveFinite(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function normCur(c) {
  if (c == null || c === "") return "";
  return String(c).trim().toUpperCase();
}

function fareDetailsCurrency(deal) {
  const root = deal && typeof deal === "object" ? deal : {};
  const cur =
    root.fareDetails?.currency ||
    root.fullDeal?.fareDetails?.currency ||
    root.dealRoot?.fareDetails?.currency ||
    null;
  return normCur(cur);
}

/**
 * מטבע מקור לתצוגה משנית — לא ILS (מטבע תצוגה ראשי).
 */
function resolveSecondaryCurrency(deal, snap, otf) {
  const tries = [
    snap?.supplier_currency,
    snap?.supplierCurrency,
    snap?.sourceCurrency,
    fareDetailsCurrency(deal),
    otf?.fx_currency,
    otf?.originalCurrency,
  ];
  for (const raw of tries) {
    const c = normCur(raw);
    if (c && c !== "ILS" && c !== "NIS") return c;
  }
  const snapCur = normCur(snap?.currency);
  if (snapCur && snapCur !== "ILS" && snapCur !== "NIS") return snapCur;
  return "USD";
}

/**
 * האם אפשר להתייחס ל-originalCurrencyPrice כמחיר בדולרים (לא להציג יורו כ-$).
 */
function isUsdPriceContext(snap, fareCur, otf) {
  const sc = snap?.currency != null ? normCur(snap.currency) : "";
  if (sc === "USD") return true;
  if (fareCur === "USD") return true;
  const oc = otf?.originalCurrency != null ? normCur(otf.originalCurrency) : "";
  if (oc === "USD") return true;
  return false;
}

/**
 * שער: כמה יחידות ILS ל-1 יחידת מטבע מקור.
 */
function pickIlsPerOneUnit(snap, otf, currency, appRates) {
  const cur = normCur(currency);
  if (!cur) return { rate: null, source: null };

  const rates = snap?.exchange_rates_used;
  if (rates && typeof rates === "object") {
    const r = rates[cur] ?? rates[String(cur).toLowerCase()];
    const n = toPositiveFinite(r);
    if (n != null) {
      return { rate: n, source: `pricingSnapshot.exchange_rates_used.${cur}` };
    }
  }

  const oc = otf?.originalCurrency != null ? normCur(otf.originalCurrency) : "";
  const fx = otf?.fx_currency != null ? normCur(otf.fx_currency) : "";
  if (oc === cur || fx === cur) {
    const n = toPositiveFinite(otf?.currencyRate);
    if (n != null) return { rate: n, source: "ourTotalFare.currencyRate" };
  }

  if (appRates && typeof appRates === "object") {
    const n = toPositiveFinite(appRates[cur] ?? appRates[String(cur).toLowerCase()]);
    if (n != null) return { rate: n, source: `AppContext.${cur}` };
  }
  return { rate: null, source: null };
}

function pickUsdIlsPerOneUsd(snap, otf, appRates) {
  return pickIlsPerOneUnit(snap, otf, "USD", appRates);
}

function resolveUsdSecondary(our, snap, otf, fareCur, ils, appRates, preferIlsDerived) {
  let usd = null;
  let source = null;
  let exchangeRate = null;

  const set = (v, src) => {
    const n = toPositiveFinite(v);
    if (n != null && usd == null) {
      usd = n;
      source = src;
    }
  };

  // FIXED_PER_PERSON: BE/search snapshots often still hold LDS×HR% USD (e.g. 6230)
  // while the sticky ILS already embeds supplier/HR fixed. Prefer ILS÷rate.
  if (!preferIlsDerived) {
    set(snap?.priceWithCommissionUSD, "pricingSnapshot.priceWithCommissionUSD");
    set(our?.priceWithCommissionUSD, "ourPrice.priceWithCommissionUSD");

    if (isUsdPriceContext(snap, fareCur, otf)) {
      set(snap?.originalCurrencyPrice, "pricingSnapshot.originalCurrencyPrice");
    }

    set(otf?.priceWithCommissionUSD, "ourTotalFare.priceWithCommissionUSD");
    set(otf?.priceWithCommissionAndCreditCardFeeUSD, "ourTotalFare.priceWithCommissionAndCreditCardFeeUSD");
    set(snap?.final_price_usd, "pricingSnapshot.final_price_usd");
  }

  if (usd == null && ils != null) {
    const { rate, source: rateSrc } = pickUsdIlsPerOneUsd(snap, otf, appRates);
    if (rate != null && rate > 0) {
      usd = ils / rate;
      exchangeRate = rate;
      source = `fallback:displayedIls/${rateSrc}`;
    }
  }

  // Snapshot USD is often the pre-HR supplier amount. Once ILS is after the
  // agent's HR, keep the dollar on the same number (no $3,164 next to ₪10,199).
  if (usd != null && ils != null) {
    const { rate, source: rateSrc } = pickUsdIlsPerOneUsd(snap, otf, appRates);
    if (rate != null && rate > 0) {
      const impliedIls = usd * rate;
      if (Math.abs(impliedIls - ils) / ils > 0.015) {
        usd = ils / rate;
        exchangeRate = rate;
        source = `displayedIls/${rateSrc}`;
      }
    }
  }

  return { amount: usd, source, exchangeRate };
}

function resolveNonUsdSecondary(snap, otf, currency, ils, appRates) {
  const cur = normCur(currency);
  let amount = null;
  let source = null;
  let exchangeRate = null;

  const set = (v, src) => {
    const n = toPositiveFinite(v);
    if (n != null && amount == null) {
      amount = n;
      source = src;
    }
  };

  const snapCur = normCur(snap?.currency);
  if (snapCur === cur || snapCur === "" || snapCur === "ILS" || snapCur === "NIS") {
    set(snap?.originalCurrencyPrice, "pricingSnapshot.originalCurrencyPrice");
  }
  set(snap?.supplier_price_original, "pricingSnapshot.supplier_price_original");
  set(otf?.ldsPrice, "ourTotalFare.ldsPrice");

  if (amount == null && ils != null) {
    const { rate, source: rateSrc } = pickIlsPerOneUnit(snap, otf, cur, appRates);
    if (rate != null && rate > 0) {
      amount = ils / rate;
      exchangeRate = rate;
      source = `fallback:displayedIls/${rateSrc}`;
    }
  }

  return { amount, source, exchangeRate };
}

function isSupplierFixedPerPersonDeal(deal, our, otf) {
  if (String(otf?.hrMarginPricingMode || "").toLowerCase() === "fixed_per_person_embedded") {
    return true;
  }
  const ct = String(
    our?.ldsCommissionCondition?.commission_type ||
      deal?.ldsCommissionCondition?.commission_type ||
      deal?.ourPrice?.ldsCommissionCondition?.commission_type ||
      ""
  )
    .trim()
    .toUpperCase();
  return ct === "FIXED_PER_PERSON";
}

/**
 * @param {object|null|undefined} deal — שורת חיפוש / raw מהקונטקסט
 * @param {number|null|undefined} ilsDisplayed — סכום בש"ח כפי שמוצג בכרטיס (ל-fallback חלוקה בלבד)
 * @param {{ USD?: number, EUR?: number, GBP?: number }|null|undefined} [appRates] — שערי AppContext (SSOT חי)
 * @returns {{
 *   ils: number|null,
 *   usd: number|null,
 *   amount: number|null,
 *   currency: string,
 *   source: string|null,
 *   exchangeRate: number|null,
 * }}
 */
export function resolveOrganizedTourDualPrice(deal, ilsDisplayed, appRates) {
  const our = pickOurPrice(deal);
  const snap = our?.pricingSnapshot && typeof our.pricingSnapshot === "object" ? our.pricingSnapshot : null;
  const otf = our?.ourTotalFare && typeof our.ourTotalFare === "object" ? our.ourTotalFare : null;
  const fareCur = fareDetailsCurrency(deal);
  const ils = toPositiveFinite(ilsDisplayed);
  const currency = resolveSecondaryCurrency(deal, snap, otf);
  const rates = appRates && typeof appRates === "object" ? appRates : null;
  const preferIlsDerived = isSupplierFixedPerPersonDeal(deal, our, otf);

  const resolved =
    currency === "USD"
      ? resolveUsdSecondary(our, snap, otf, fareCur, ils, rates, preferIlsDerived)
      : resolveNonUsdSecondary(snap, otf, currency, ils, rates);

  const amount = resolved.amount;
  return {
    ils: ilsDisplayed != null && Number.isFinite(Number(ilsDisplayed)) ? Number(ilsDisplayed) : null,
    /** @deprecated העדף amount + currency — נשאר ל-compat כשמטבע משני הוא USD */
    usd: currency === "USD" ? amount : null,
    amount,
    currency,
    source: resolved.source,
    exchangeRate: resolved.exchangeRate,
  };
}

/**
 * פורמט תצוגה למטבע משני לפי קוד מטבע מקור.
 * @param {number|null|undefined} amount
 * @param {string|null|undefined} currency
 * @returns {string|null}
 */
export function formatOrganizedTourSecondaryCurrencyDisplay(amount, currency) {
  const n = toPositiveFinite(amount);
  if (n == null) return null;
  const cur = normCur(currency) || "USD";
  const formatted = n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (cur === "EUR") return `€${formatted}`;
  if (cur === "GBP") return `£${formatted}`;
  if (cur === "USD") return `$${formatted}`;
  return `${formatted} ${cur}`;
}

/**
 * פורמט תצוגה לדולר — שני ספרות אחרי הנקודה, פסיקים לפי en-US.
 * @param {number|null|undefined} usd
 * @returns {string|null}
 */
export function formatOrganizedTourUsdDisplay(usd) {
  return formatOrganizedTourSecondaryCurrencyDisplay(usd, "USD");
}

/**
 * One display pair for organized-tour profile: rounded package ILS + the same
 * secondary currency string the sticky sidebar uses (no ceil / no integer USD).
 * @param {object|null|undefined} deal
 * @param {number|null|undefined} ils
 * @param {object|null|undefined} [appRates]
 * @returns {{ ilsRounded: number, secondary: string|null, amount: number|null, currency: string }|null}
 */
export function formatOrganizedTourPackageMoney(deal, ils, appRates) {
  const n = Number(ils);
  if (!Number.isFinite(n)) return null;
  const ilsRounded = Math.round(n);
  const dual = resolveOrganizedTourDualPrice(deal, ilsRounded, appRates);
  const amount = dual.amount ?? dual.usd;
  return {
    ilsRounded,
    secondary: formatOrganizedTourSecondaryCurrencyDisplay(amount, dual.currency),
    amount: amount != null && Number.isFinite(Number(amount)) ? Number(amount) : null,
    currency: dual.currency,
  };
}
